import classes from 'modules/list/listPage/ListLoadingPlaceholder/styles/genericPublicListLoadingPlaceholder.module.css';
import Button from '../../../../components/Button';
import { ControlGroupSection } from '../../../../components/ControlGroup';
import FlexGrid, { FlexGridColumn } from '../../../../components/FlexGrid';
import { MovingGradient } from '../../../../components/MovingGradient';
import PaddingBox from '../../../../components/PaddingBox';
import Typography from '../../../../components/Typography';
import useMediaQuery from '../../../../hooks/useMedia';
import { isMobileAny, minTableViewWidth } from '../../../../utils/device';
import { useTranslationAPI } from '../../../translations';
import { useUserPageContextState } from '../../../user';
import { ListFilterBreadcrumbCollapseProvider } from '../../components/ListFilterBreadcrumb/ListFilterBreadcrumbCollapseProvider';
import { ListFilterBreadcrumbTemp } from '../../components/ListFilterBreadcrumb/ListFilterBreadcrumbTemp';
import { ListLayoutBar, ListLayoutBarItem, ListLayoutBarLeft, ListLayoutBarRow } from '../../components/ListLayout';
import { ListLoadingPlaceholderSidebar } from './ListLoadingPlaceholderSidebar';
import { ListLoadingPlaceholderSidebarItem } from './ListLoadingPlaceholderSidebarItem';

type GenericPublicListLoadingPlaceholderProps = {
	hasSidebar?: boolean;
	headerTitleCode?: number;
	findButtonTitleCode?: number;
	headerSearchFieldsCount?: number;
};

const listItems = Array.from(Array(5).keys());

export const TileSkeleton = () => {
	return (
		<div
			style={{
				backgroundColor: '#FFF',
				borderRadius: '16px',
				border: '1px solid var(--oc-gray-4)',
				padding: '16px',
				boxSizing: 'border-box',
			}}
		>
			<FlexGrid
				grow
				column
				gap='small'
			>
				<FlexGridColumn noGrow>
					<PaddingBox bottomTiny>
						<MovingGradient
							height='12px'
							width='55%'
							borderRadius='4px'
						/>
					</PaddingBox>
				</FlexGridColumn>
				<FlexGridColumn grow>
					<PaddingBox bottomTiny>
						<MovingGradient
							height='12px'
							width='100%'
							borderRadius='4px'
						/>
					</PaddingBox>
					<PaddingBox bottomTiny>
						<MovingGradient
							height='12px'
							width='100%'
							borderRadius='4px'
						/>
					</PaddingBox>
					<MovingGradient
						height='12px'
						width='35%'
						borderRadius='4px'
					/>
				</FlexGridColumn>
			</FlexGrid>
		</div>
	);
};

function GenericPublicListLoadingPlaceholder({
	hasSidebar,
	headerTitleCode,
	findButtonTitleCode,
	headerSearchFieldsCount = 1,
}: GenericPublicListLoadingPlaceholderProps) {
	const { isAuthenticated } = useUserPageContextState();
	const { translate } = useTranslationAPI();
	const matchColumnViewQuery = useMediaQuery(`screen and (max-width: ${minTableViewWidth}px)`);

	return (
		<div className={classes.layout}>
			<div
				style={{
					height: '100%',
					width: '100%',
				}}
			>
				<FlexGrid
					column
					hrCenter
					center
					style={{
						backdropFilter: 'blur(4px)',
						height: '100%',
						width: '100%',
						background: 'rgba(0, 0, 0, 0.6)',
					}}
				>
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: '1fr',
							gridTemplateRows: 'auto auto',
							rowGap: '16px',
							justifyItems: 'stretch',
							width: '100%',
							maxWidth: '943px',
							margin: '0 auto',
							padding: '8px',
							boxSizing: 'border-box',
						}}
					>
						<Typography
							headingSize={1}
							fontSize='28px'
							bold
							color='#FFF'
							align='center'
						>
							{headerTitleCode && translate(headerTitleCode)}
							{!headerTitleCode && '...'}
						</Typography>
						<div>
							<FlexGrid
								column={matchColumnViewQuery}
								gap={matchColumnViewQuery ? 'small' : undefined}
							>
								{Array.from(Array(headerSearchFieldsCount).keys()).map((col) => (
									<FlexGridColumn
										grow
										basis50
										key={col}
									>
										<MovingGradient
											height='40px'
											width='100%'
										/>
									</FlexGridColumn>
								))}
								<FlexGridColumn>
									<Button
										block={matchColumnViewQuery}
										primary
										disabled
										style={{
											whiteSpace: 'nowrap',
										}}
									>
										{findButtonTitleCode && translate(findButtonTitleCode)}
										{!findButtonTitleCode && '...'}
									</Button>
								</FlexGridColumn>
							</FlexGrid>
						</div>
					</div>
				</FlexGrid>
			</div>
			<PaddingBox
				className={classes.widthBox}
				small={!isMobileAny}
				tiny={isMobileAny}
			>
				<FlexGrid gap='small'>
					<div className={classes.main}>
						<ListLayoutBar>
							<ListLayoutBarRow>
								<ListLayoutBarLeft>
									<ListLayoutBarItem>
										<Typography
											headingSize={1}
											fontSize='16px'
											bold
										>
											<MovingGradient
												height='18px'
												width='80px'
											/>
										</Typography>
									</ListLayoutBarItem>
								</ListLayoutBarLeft>
							</ListLayoutBarRow>
							<ListLayoutBarRow>
								<ListLayoutBarLeft>
									<ListLayoutBarItem>
										<ListFilterBreadcrumbCollapseProvider>
											<ListFilterBreadcrumbTemp />
										</ListFilterBreadcrumbCollapseProvider>
									</ListLayoutBarItem>
								</ListLayoutBarLeft>
							</ListLayoutBarRow>
						</ListLayoutBar>
						<FlexGrid
							column
							gap='tiny'
						>
							{listItems.map((item) => (
								<TileSkeleton key={item} />
							))}
						</FlexGrid>
					</div>
					{!isMobileAny && hasSidebar && isAuthenticated && <ListLoadingPlaceholderSidebar />}
					{!isMobileAny && hasSidebar && !isAuthenticated && (
						<FlexGrid
							column
							gap='normal'
							style={{
								flex: '0 0 283px',
								minWidth: '283px',
							}}
						>
							<div
								style={{
									backgroundColor: '#FFF',
									borderRadius: '16px',
									padding: '16px',
								}}
							>
								<ControlGroupSection>
									{Array.from({ length: 7 }).map((_, index) => (
										<ListLoadingPlaceholderSidebarItem key={index} />
									))}
								</ControlGroupSection>
							</div>
						</FlexGrid>
					)}
				</FlexGrid>
			</PaddingBox>
		</div>
	);
}

export { GenericPublicListLoadingPlaceholder };
